import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useSalon } from "../lib/salon";
import { formatDateTime, formatDate } from "../lib/time";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { Plus, MagnifyingGlass, Archive, ArrowCounterClockwise, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useRefresh } from "../context/RefreshContext";
import PhoneInput from "../components/PhoneInput";

const fmt = formatDateTime;
function fmtRelative(iso) {
  // Relative time still uses real elapsed wall-clock seconds via Date.parse
  // — the underlying ISO is naive-salon-local but the delta against "now"
  // is meaningful regardless. Falls back to a wall-clock date string.
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const ms = Date.now() - t;
  const min = Math.round(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.round(hr / 24);
  if (d < 30) return `${d}d ago`;
  return formatDate(iso);
}

const FILTERS = [
  { key: "active",   label: "Active"   },
  { key: "archived", label: "Archived" },
  { key: "all",      label: "All"      },
];

export default function Customers() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [include, setInclude] = useState("active");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [profileFor, setProfileFor] = useState(null);
  const { bookingsVersion } = useRefresh();
  const salon = useSalon();
  const currency = salon?.currency_symbol || "$";

  const fetchAll = async () => {
    const params = { include };
    if (q) params.q = q;
    const { data } = await api.get("/customers", { params });
    setList(data);
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [q, include, bookingsVersion]);

  const submit = async () => {
    try {
      if (editing) await api.put(`/customers/${editing.id}`, form);
      else await api.post("/customers", form);
      toast.success(editing ? "Customer updated" : "Customer created");
      setCreating(false); setEditing(null);
      setForm({ name: "", phone: "", email: "" });
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const archive = async (c) => {
    try {
      await api.post(`/customers/${c.id}/archive`);
      toast.success(`${c.name} archived`);
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };
  const restore = async (c) => {
    try {
      await api.post(`/customers/${c.id}/restore`);
      toast.success(`${c.name} restored`);
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const startEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, phone: c.phone, email: c.email || "" });
    setCreating(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">Customers</div>
          <h1 className="font-heading text-4xl tracking-tight font-light mt-2">Your guests</h1>
        </div>
        <Button
          onClick={() => { setEditing(null); setForm({ name: "", phone: "", email: "" }); setCreating(true); }}
          className="bg-primary hover:bg-[#2B4F39]"
          data-testid="customers-new-btn"
        >
          <Plus size={16} className="mr-1" /> New customer
        </Button>
      </div>

      <div className="surface-card p-4 flex items-center gap-3 flex-wrap">
        <MagnifyingGlass size={16} className="text-muted-foreground" />
        <Input
          placeholder="Search by name or phone"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-md bg-[#F9F8F6]"
          data-testid="customers-search"
        />
        <div className="flex gap-1 ml-auto">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setInclude(f.key)}
              data-testid={`customers-filter-${f.key}`}
              className={`px-3 py-1.5 text-xs rounded-md border transition-all ${
                include === f.key ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border hover:border-primary"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="surface-card overflow-hidden">
        <table className="w-full text-sm" data-testid="customers-table">
          <thead className="bg-secondary/40">
            <tr className="text-left">
              <th className="p-3 overline">Name</th>
              <th className="p-3 overline">Phone</th>
              <th className="p-3 overline">Email</th>
              <th className="p-3 overline">Status</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan={5} className="p-12 text-center text-muted-foreground">No customers.</td></tr>
            )}
            {list.map((c) => (
              <tr
                key={c.id}
                className={`border-t border-border hover:bg-secondary/30 ${c.archived ? "opacity-60" : ""}`}
                data-testid={`customer-row-${c.id}`}
              >
                <td className="p-3 font-medium">{c.name}</td>
                <td className="p-3 font-mono text-xs">{c.phone}</td>
                <td className="p-3 text-muted-foreground">{c.email || "—"}</td>
                <td className="p-3">
                  {c.archived ? (
                    <span className="inline-block px-2 py-1 rounded-md text-xs bg-[#EFEBE3] text-muted-foreground">archived</span>
                  ) : (
                    <span className="inline-block px-2 py-1 rounded-md text-xs bg-[#D1E0D7] text-[#1A3626]">active</span>
                  )}
                </td>
                <td className="p-3 text-right whitespace-nowrap space-x-1">
                  <Button variant="ghost" size="sm" onClick={() => setProfileFor(c)} data-testid={`customer-profile-${c.id}`}>Profile</Button>
                  <Button variant="ghost" size="sm" onClick={() => startEdit(c)} data-testid={`customer-edit-${c.id}`}>Edit</Button>
                  {c.archived ? (
                    <Button variant="ghost" size="sm" onClick={() => restore(c)} data-testid={`customer-restore-${c.id}`} className="text-[#1A3626]">
                      <ArrowCounterClockwise size={14} className="mr-1" /> Restore
                    </Button>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={() => archive(c)} data-testid={`customer-archive-${c.id}`} className="text-muted-foreground">
                      <Archive size={14} className="mr-1" /> Archive
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create/edit */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="bg-white" data-testid="customer-form-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">{editing ? "Edit customer" : "New customer"}</DialogTitle>
            <DialogDescription>Customers are identified by phone number. Operational notes live on the customer profile.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label className="overline">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#F9F8F6]" data-testid="customer-name-input" /></div>
            <div className="space-y-2"><Label className="overline">Phone</Label><PhoneInput value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#F9F8F6]" data-testid="customer-phone-input" /></div>
            <div className="space-y-2"><Label className="overline">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#F9F8F6]" data-testid="customer-email-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={submit} className="bg-primary hover:bg-[#2B4F39]" data-testid="customer-submit-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CustomerProfileDialog customer={profileFor} onClose={() => setProfileFor(null)} />
    </div>
  );
}

function CustomerProfileDialog({ customer, onClose }) {
  const salon = useSalon();
  const currency = salon?.currency_symbol || "$";
  const [tab, setTab] = useState("history");
  const [history, setHistory] = useState([]);
  const [notes, setNotes] = useState([]);
  const [draft, setDraft] = useState("");
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingDraft, setEditingDraft] = useState("");

  useEffect(() => {
    if (!customer) return;
    setTab("history");
    setDraft("");
    setEditingNoteId(null);
    Promise.all([
      api.get(`/customers/${customer.id}/history`).then((r) => r.data),
      api.get(`/customers/${customer.id}/notes`).then((r) => r.data),
    ]).then(([h, n]) => { setHistory(h); setNotes(n); }).catch(() => {});
  }, [customer]);

  const addNote = async () => {
    const body = draft.trim();
    if (!body) return;
    try {
      const { data } = await api.post(`/customers/${customer.id}/notes`, { body });
      setNotes((prev) => [data, ...prev]);
      setDraft("");
      toast.success("Note added");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const saveEdit = async (noteId) => {
    const body = editingDraft.trim();
    if (!body) return;
    try {
      const { data } = await api.put(`/customers/${customer.id}/notes/${noteId}`, { body });
      setNotes((prev) => prev.map((n) => (n.id === noteId ? data : n)));
      setEditingNoteId(null);
      toast.success("Note updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await api.delete(`/customers/${customer.id}/notes/${noteId}`);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      toast.success("Note deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <Dialog open={!!customer} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-2xl" data-testid="customer-profile-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight flex items-center gap-3">
            {customer?.name}
            {customer?.archived && (
              <span className="text-xs font-normal px-2 py-1 rounded-md bg-[#EFEBE3] text-muted-foreground" data-testid="profile-archived-badge">archived</span>
            )}
          </DialogTitle>
          <DialogDescription className="font-mono">{customer?.phone}</DialogDescription>
        </DialogHeader>

        {/* Tabs */}
        <div className="border-b border-border flex gap-1" data-testid="profile-tabs">
          <button
            onClick={() => setTab("history")}
            data-testid="profile-tab-history"
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
              tab === "history" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            Booking history
            <span className="ml-2 text-xs text-muted-foreground">{history.length}</span>
          </button>
          <button
            onClick={() => setTab("notes")}
            data-testid="profile-tab-notes"
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
              tab === "notes" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            Notes
            <span className="ml-2 text-xs text-muted-foreground">{notes.length}</span>
          </button>
        </div>

        <div className="max-h-[60vh] overflow-auto scrollbar-thin">
          {tab === "history" && (
            <div data-testid="profile-history-panel">
              {history.length === 0 ? (
                <div className="text-sm text-muted-foreground p-6 text-center">No appointments yet.</div>
              ) : (
                <ul className="divide-y divide-border">
                  {history.map((b) => (
                    <li key={b.id} className="py-3 flex items-start gap-3" data-testid={`history-item-${b.id}`}>
                      <div className="w-1 h-12 rounded-full mt-1" style={{ background: b.stylist?.color || "#1A3626" }} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{b.service?.name} · {b.stylist?.name}</div>
                        <div className="text-xs text-muted-foreground">{fmt(b.start_at)} · <span className="capitalize">{b.status}</span> · {b.duration_minutes} min</div>
                        {b.notes && (
                          <div className="text-xs mt-1.5 px-2.5 py-1.5 rounded-md bg-[#F9F8F6] border border-border" data-testid={`history-booking-notes-${b.id}`}>
                            <span className="overline mr-1.5">Booking note:</span> {b.notes}
                          </div>
                        )}
                      </div>
                      <div className="text-sm font-mono">{currency}{b.price?.toFixed?.(0) ?? 0}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {tab === "notes" && (
            <div className="space-y-3" data-testid="profile-notes-panel">
              <div className="space-y-2 sticky top-0 bg-white pb-2">
                <Textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Allergies, formulas, preferences, reminders..."
                  className="bg-[#F9F8F6] min-h-[64px]"
                  maxLength={2000}
                  data-testid="profile-note-input"
                />
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[11px] text-muted-foreground">{draft.length} / 2000</div>
                  <Button
                    onClick={addNote}
                    disabled={!draft.trim()}
                    className="bg-primary hover:bg-[#2B4F39]"
                    size="sm"
                    data-testid="profile-note-add-btn"
                  >
                    Add note
                  </Button>
                </div>
              </div>

              {notes.length === 0 ? (
                <div className="text-sm text-muted-foreground p-6 text-center">No notes yet. The first one's the hardest.</div>
              ) : (
                <ul className="space-y-2.5">
                  {notes.map((n) => (
                    <li
                      key={n.id}
                      className="px-3.5 py-2.5 rounded-md border border-border bg-[#F9F8F6]"
                      data-testid={`profile-note-${n.id}`}
                    >
                      {editingNoteId === n.id ? (
                        <div className="space-y-2">
                          <Textarea
                            value={editingDraft}
                            onChange={(e) => setEditingDraft(e.target.value)}
                            className="bg-white min-h-[60px]"
                            maxLength={2000}
                            data-testid={`profile-note-edit-input-${n.id}`}
                          />
                          <div className="flex justify-end gap-1.5">
                            <Button variant="ghost" size="sm" onClick={() => setEditingNoteId(null)}>Cancel</Button>
                            <Button size="sm" className="bg-primary hover:bg-[#2B4F39]" onClick={() => saveEdit(n.id)} data-testid={`profile-note-save-${n.id}`}>Save</Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="text-sm whitespace-pre-wrap leading-relaxed">{n.body}</div>
                          <div className="flex items-center justify-between mt-1.5">
                            <div className="text-[11px] text-muted-foreground">
                              {n.author_name && <span className="mr-1">{n.author_name}</span>}
                              <span title={fmt(n.created_at)}>· {fmtRelative(n.created_at)}</span>
                              {n.updated_at && n.updated_at !== n.created_at && <span className="ml-1">· edited</span>}
                            </div>
                            <div className="flex items-center gap-0.5">
                              <button
                                onClick={() => { setEditingNoteId(n.id); setEditingDraft(n.body); }}
                                data-testid={`profile-note-edit-${n.id}`}
                                aria-label="Edit note"
                                className="p-1.5 hover:bg-secondary rounded-md text-muted-foreground hover:text-foreground"
                              >
                                <PencilSimple size={13} />
                              </button>
                              <button
                                onClick={() => deleteNote(n.id)}
                                data-testid={`profile-note-delete-${n.id}`}
                                aria-label="Delete note"
                                className="p-1.5 hover:bg-destructive/10 rounded-md text-muted-foreground hover:text-destructive"
                              >
                                <Trash size={13} />
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
